import { useEffect, useState, useMemo, useRef } from "react";
import axios, { API } from "@/lib/api";
import { toast } from "sonner";
import { getBackendUrl } from "@/lib/backend";
import { formatApiError } from "@/lib/apiError";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Wand2, PencilRuler, LayoutGrid, ImageIcon, RefreshCw, Check, Plus, Trash2, MousePointer2 } from "lucide-react";

const BACKEND = getBackendUrl();

/**
 * Facetra Portfolio Item Studio.
 *
 * Sequence of operations (Feb 2026):
 *   1. Dimensions — manual authoring (optional AI auto-detect).
 *   2. Multi-view — generate a fresh multi-view OR adopt an existing
 *      gallery render as the confirmed multi-view. The multi-view
 *      becomes the "locked" visual ground-truth for the piece.
 *   3. Technical drawing — OVERLAYS dimension callouts + spec table on
 *      top of the confirmed multi-view. Does NOT re-imagine geometry.
 *      Enforced by the backend (422 until a multi-view exists).
 *
 * The view count per product type also varies (necklace=1, ring=3, etc.)
 * — schema is fetched from the backend so the UI never hardcodes it.
 */
export default function PortfolioItemStudio({
  open,
  onOpenChange,
  portfolio,
  item,
  onChanged,
}) {
  const [tab, setTab] = useState("dimensions");
  const [schema, setSchema] = useState(null);
  const [productType, setProductType] = useState("ring");
  const [dims, setDims] = useState({});
  const [saving, setSaving] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [renderingMv, setRenderingMv] = useState(false);
  const [adopting, setAdopting] = useState(false);
  const [drawingUrl, setDrawingUrl] = useState(item?.technical_drawing_url || "");
  const [multiviewUrl, setMultiviewUrl] = useState(item?.multiview_url || "");
  const [multiviewLocked, setMultiviewLocked] = useState(!!item?.multiview_gen_id);
  const [gallery, setGallery] = useState([]);
  const [galleryLoaded, setGalleryLoaded] = useState(false);
  // Multi-select: `pickedGenIds` is an ORDERED array of gallery ids.
  // Generate uses the first pick (or the item's own render if empty).
  // Adopt uses the whole list — 1 → single, 2..4 → PIL grid via backend.
  const initialPick = item?.multiview_source_gen_id || item?.generation_id || "";
  const [pickedGenIds, setPickedGenIds] = useState(initialPick ? [initialPick] : []);
  const [renderMode, setRenderMode] = useState(item?.technical_drawing_render_mode || "render");
  // Interactive SVG dimension editor state — see the Multi-view tab.
  const [manualCallouts, setManualCallouts] = useState([]);

  // Load the dimension schema once (cheap — the backend caches it in memory).
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/studio/portfolios/dimension-schema`)
      .then((r) => setSchema(r.data))
      .catch((e) => toast.error(formatApiError(e, "Couldn't load dimension schema.")));
  }, [open]);

  // Sync local form state whenever a different item is opened.
  useEffect(() => {
    if (!item) return;
    setProductType(item.product_type || "ring");
    setDims(item.dimensions || {});
    setDrawingUrl(item.technical_drawing_url || "");
    setMultiviewUrl(item.multiview_url || "");
    setMultiviewLocked(!!item.multiview_gen_id);
    const seed = item.multiview_source_gen_id || item.generation_id || "";
    setPickedGenIds(seed ? [seed] : []);
    setRenderMode(item.technical_drawing_render_mode || "render");
    setManualCallouts([]);
    setTab("dimensions");
  }, [item?.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Lazy-load the user's studio gallery when they first touch the
  // multi-view tab so the source picker + adopt-from-gallery is
  // populated.
  useEffect(() => {
    if (tab !== "multiview" || galleryLoaded) return;
    axios.get(`${API}/studio/generations?limit=48`)
      .then((r) => {
        // /studio/generations returns { entries, count, has_more, next_cursor }.
        // Legacy shape was `generations`. Guard both, plus the raw-array
        // shape for tests. Filter out portfolio-studio artifacts (technical
        // drawings + multiviews) so users only pick real hero renders.
        const raw = Array.isArray(r.data)
          ? r.data
          : (r.data?.entries || r.data?.generations || []);
        const list = raw.filter((g) => {
          const id = g.id || "";
          return !id.startsWith("pfdraft_") && !id.startsWith("pfmv_") && !id.startsWith("pfmb_");
        });
        setGallery(list);
        setGalleryLoaded(true);
      })
      .catch(() => setGalleryLoaded(true));
  }, [tab, galleryLoaded]);

  // Views expected for the current product type — drives the empty
  // state copy in the multi-view panel and the help text everywhere.
  const viewLabels = useMemo(() => {
    const arr = schema?.views_per_type?.[productType] || ["front", "top", "side"];
    return arr.map((v) => v.toUpperCase());
  }, [schema, productType]);

  const fields = useMemo(() => {
    if (!schema) return [];
    return schema.schema?.[productType] || [];
  }, [schema, productType]);

  const handleFieldChange = (key, value) => {
    setDims((prev) => ({ ...prev, [key]: value }));
  };

  const runAutoDetect = async () => {
    if (!item) return;
    setDetecting(true);
    try {
      const r = await axios.post(`${API}/studio/portfolios/${portfolio.id}/items/${item.id}/detect-dimensions`);
      setProductType(r.data.product_type);
      setDims((prev) => ({ ...(r.data.defaults || {}), ...(prev || {}), ...(r.data.dimensions || {}) }));
      toast.success(`AI detected: ${r.data.product_type}. Review and save.`);
    } catch (e) {
      toast.error(formatApiError(e, "Auto-detect failed."));
    } finally {
      setDetecting(false);
    }
  };

  const saveSpecs = async () => {
    if (!item) return;
    setSaving(true);
    try {
      // Numerify anything that looks like a number so backend gets floats.
      const cleaned = {};
      Object.entries(dims).forEach(([k, v]) => {
        if (v === "" || v == null) return;
        if (typeof v === "number") { cleaned[k] = v; return; }
        const num = Number(v);
        cleaned[k] = Number.isFinite(num) && String(num) === String(v).trim() ? num : v;
      });
      await axios.patch(`${API}/studio/portfolios/${portfolio.id}/items/${item.id}/specs`, {
        product_type: productType,
        dimensions: cleaned,
      });
      toast.success("Dimensions saved.");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e, "Save failed."));
    } finally {
      setSaving(false);
    }
  };

  const runTechnicalDrawing = async () => {
    if (!item) return;
    if (!multiviewLocked) {
      toast.error("Confirm a multi-view first — the technical drawing overlays dimensions on top of it.");
      setTab("multiview");
      return;
    }
    // Save first so the drawing uses the latest values.
    await saveSpecs();
    setDrafting(true);
    try {
      const r = await axios.post(
        `${API}/studio/portfolios/${portfolio.id}/items/${item.id}/technical-drawing`,
        {
          render_mode: renderMode,
          manual_callouts: manualCallouts.length ? manualCallouts : undefined,
        },
      );
      setDrawingUrl(r.data.image_url);
      toast.success(manualCallouts.length
        ? `Technical drawing ready with ${manualCallouts.length} manual callout${manualCallouts.length > 1 ? "s" : ""}.`
        : "Technical drawing ready.");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e, "Drawing generation failed."));
    } finally {
      setDrafting(false);
    }
  };

  const runMultiview = async () => {
    if (!item) return;
    await saveSpecs();
    setRenderingMv(true);
    try {
      const primary = pickedGenIds[0];
      const r = await axios.post(
        `${API}/studio/portfolios/${portfolio.id}/items/${item.id}/multiview`,
        primary && primary !== item.generation_id ? { source_gen_id: primary } : {},
      );
      setMultiviewUrl(r.data.image_url);
      setMultiviewLocked(true);
      toast.success("Multi-view rendered. Review — if it looks right, run the technical drawing.");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e, "Multi-view generation failed."));
    } finally {
      setRenderingMv(false);
    }
  };

  const adoptFromGallery = async () => {
    if (!item || pickedGenIds.length === 0) return;
    setAdopting(true);
    try {
      const r = await axios.post(
        `${API}/studio/portfolios/${portfolio.id}/items/${item.id}/multiview/adopt`,
        { gallery_gen_ids: pickedGenIds },
      );
      setMultiviewUrl(r.data.image_url);
      setMultiviewLocked(true);
      toast.success(r.data.composed
        ? `Composed ${pickedGenIds.length} renders into a locked multi-view (free).`
        : "Locked as multi-view (no credit charged).");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e, "Adopt failed."));
    } finally {
      setAdopting(false);
    }
  };

  const togglePick = (genId) => {
    setPickedGenIds((prev) => {
      if (prev.includes(genId)) return prev.filter((x) => x !== genId);
      if (prev.length >= 4) {
        toast.info("Up to 4 renders can be adopted at once.");
        return prev;
      }
      return [...prev, genId];
    });
  };

  const groupedFields = useMemo(() => {
    const groups = { structure: [], stone: [], size: [] };
    fields.forEach((f) => { (groups[f.category] || groups.structure).push(f); });
    return groups;
  }, [fields]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="portfolio-item-studio"
        className="max-w-5xl bg-[#0A0A0A] border border-white/10 text-white p-0 overflow-hidden"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-white/8">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#141414] border border-white/10 overflow-hidden flex-shrink-0">
              {item?.image_url && (
                <img src={`${BACKEND}${item.image_url}`} alt="" className="w-full h-full object-cover" />
              )}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">
                {item?.design_number} · {(item?.style || "—")}
              </div>
              <DialogTitle className="text-white font-display text-xl">Piece Studio</DialogTitle>
            </div>
          </div>

          <div className="mt-4 flex gap-1 bg-[#111] border border-white/8 p-1 rounded-xl w-fit">
            <TabBtn active={tab === "dimensions"} onClick={() => setTab("dimensions")} icon={PencilRuler} label="1 · Dimensions" testId="pfs-tab-dimensions" />
            <TabBtn active={tab === "multiview"} onClick={() => setTab("multiview")} icon={LayoutGrid} label="2 · Multi-view" testId="pfs-tab-multiview" />
            <TabBtn active={tab === "drawing"} onClick={() => setTab("drawing")} icon={Wand2} label="3 · Technical drawing" testId="pfs-tab-drawing" locked={!multiviewLocked} />
          </div>
        </DialogHeader>

        <div className="p-6 max-h-[65vh] overflow-y-auto">
          {tab === "dimensions" && (
            <div className="space-y-5" data-testid="pfs-dimensions-panel">
              <div className="flex flex-wrap items-center gap-3">
                <label className="text-xs text-white/60 flex-shrink-0">Product type</label>
                <select
                  data-testid="pfs-product-type"
                  value={productType}
                  onChange={(e) => setProductType(e.target.value)}
                  className="bg-[#141414] border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white capitalize focus:outline-none focus:border-[#D4AF37]/50"
                >
                  {(schema?.product_types || ["ring"]).map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={runAutoDetect}
                  disabled={detecting}
                  data-testid="pfs-autodetect"
                  className="ml-auto inline-flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30 hover:bg-[#D4AF37]/20 transition disabled:opacity-50"
                >
                  {detecting ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Detecting…</> : <><Wand2 className="w-3.5 h-3.5" /> Auto-detect with AI · 1 credit</>}
                </button>
              </div>

              {(["structure", "stone", "size"]).map((cat) => (
                groupedFields[cat].length > 0 && (
                  <div key={cat}>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">{cat}</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {groupedFields[cat].map((f) => (
                        <label key={f.key} className="block" data-testid={`pfs-field-${f.key}`}>
                          <span className="text-[11px] text-white/60">{f.label}</span>
                          <div className="mt-1 flex items-center gap-1 bg-[#141414] border border-white/10 rounded-lg px-2.5 py-1.5 focus-within:border-[#D4AF37]/50">
                            <input
                              type="text"
                              inputMode={f.unit === "US" ? "text" : "decimal"}
                              value={dims[f.key] ?? ""}
                              onChange={(e) => handleFieldChange(f.key, e.target.value)}
                              placeholder={String(f.default ?? "")}
                              className="w-full bg-transparent text-sm text-white outline-none placeholder-white/25"
                            />
                            <span className="text-[10px] text-white/40 flex-shrink-0">{f.unit}</span>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )
              ))}

              <div className="flex items-center gap-3 pt-2">
                <button
                  type="button"
                  onClick={saveSpecs}
                  disabled={saving}
                  data-testid="pfs-save"
                  className="inline-flex items-center gap-1.5 text-xs px-4 py-2 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#F3CE5A] transition disabled:opacity-50"
                >
                  {saving ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</> : "Save dimensions"}
                </button>
                <span className="text-[10px] text-white/40">Manual is primary. Auto-detect is optional (1 credit).</span>
              </div>
            </div>
          )}

          {tab === "drawing" && (
            <div className="space-y-4" data-testid="pfs-drawing-panel">
              <div className="rounded-xl border border-[#D4AF37]/20 bg-[#D4AF37]/[0.04] p-3">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-1">Step 3 of 3 · Technical drawing</div>
                <p className="text-[12px] text-white/60 leading-relaxed">
                  Overlays ISO-style extension lines, dimension arrows and a bordered spec table <b className="text-white">on top of your confirmed multi-view</b>. No re-imagining — the underlying product stays pixel-identical to what you locked in step 2.
                </p>
              </div>

              {!multiviewLocked ? (
                <div className="rounded-xl border border-amber-400/25 bg-amber-400/[0.04] p-6 text-center" data-testid="pfs-drawing-blocked">
                  <p className="text-sm text-amber-200 font-medium">Confirm a multi-view first</p>
                  <p className="text-[12px] text-white/55 mt-1">The technical drawing overlays dimensions on the confirmed multi-view. Head back to Step 2 to generate or adopt one.</p>
                  <button
                    type="button"
                    onClick={() => setTab("multiview")}
                    data-testid="pfs-drawing-goto-mv"
                    className="mt-3 text-[11px] text-[#D4AF37] hover:text-[#F3CE5A] inline-flex items-center gap-1"
                  >
                    ← Go to Multi-view
                  </button>
                </div>
              ) : (
                <>
                  {/* Render-mode toggle */}
                  <div className="flex items-center gap-3 text-[11px]">
                    <span className="text-white/50 uppercase tracking-[0.22em] text-[10px]">Style</span>
                    <div className="inline-flex bg-[#111] border border-white/8 rounded-full p-0.5">
                      <button
                        type="button"
                        onClick={() => setRenderMode("render")}
                        data-testid="pfs-drawing-mode-render"
                        className={`px-3 py-1 rounded-full transition ${renderMode === "render" ? "bg-[#D4AF37]/20 text-[#D4AF37]" : "text-white/50 hover:text-white"}`}
                      >
                        Render (colour)
                      </button>
                      <button
                        type="button"
                        onClick={() => setRenderMode("line")}
                        data-testid="pfs-drawing-mode-line"
                        className={`px-3 py-1 rounded-full transition ${renderMode === "line" ? "bg-[#D4AF37]/20 text-[#D4AF37]" : "text-white/50 hover:text-white"}`}
                      >
                        Line drawing
                      </button>
                    </div>
                    <span className="text-white/35">
                      {renderMode === "line" ? "Multi-view is edge-detected to line art before dimensions are drawn." : "Keeps the multi-view's colour render underneath the dimension lines."}
                    </span>
                  </div>

                  <ArtifactPanel
                    testId="pfs-drawing-panel-inner"
                    url={drawingUrl}
                    busy={drafting}
                    busyLabel={renderMode === "line" ? "Redrawing as CAD line art + adding callouts…" : "Overlaying dimensions on the multi-view…"}
                    emptyIcon={PencilRuler}
                    emptyTitle="No technical drawing yet"
                    emptyBody="Dimension callouts (ISO extension lines + numeric values) are drawn directly on top of the confirmed multi-view. Bill of Materials appears in the exported Lookbook PDF under each hero product."
                    buttonLabel={renderMode === "line" ? "Generate line-art drawing · 1 credit" : "Generate technical drawing · 1 credit"}
                    rerunLabel={manualCallouts.length ? `Re-generate with ${manualCallouts.length} manual callout${manualCallouts.length > 1 ? "s" : ""} · 1 credit` : "Re-generate · 1 credit"}
                    onRun={runTechnicalDrawing}
                    onSave={saveSpecs}
                    savedProductType={productType}
                  />

                  {/* Interactive SVG dimension editor — user places arrows
                      directly on the multi-view so callouts land exactly
                      where they want. Burnt in by PIL before Gemini gets
                      it, so no fresh Gemini call is needed to reposition. */}
                  <SvgDimensionEditor
                    multiviewUrl={multiviewUrl}
                    callouts={manualCallouts}
                    onChange={setManualCallouts}
                  />
                </>
              )}
            </div>
          )}

          {tab === "multiview" && (
            <div className="space-y-4" data-testid="pfs-multiview-panel">
              <div className="rounded-xl border border-[#D4AF37]/20 bg-[#D4AF37]/[0.04] p-3">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-1">Step 2 of 3 · Multi-view</div>
                <p className="text-[12px] text-white/60 leading-relaxed">
                  Lock the reference geometry for this piece. Either <b className="text-white">generate a fresh multi-view</b> (1 credit) from a chosen source render, or <b className="text-white">adopt an existing gallery render</b> as the multi-view (free — no credit). Once confirmed, the technical drawing overlays dimensions on top of it without re-imagining the product.
                </p>
                <div className="mt-2 text-[11px] text-white/45">
                  Required views for <b className="text-white/70 capitalize">{productType}</b>: <span className="text-[#D4AF37]">{viewLabels.join(" · ")}</span>
                </div>
              </div>

              {/* Gallery source picker — multi-select. Generate uses the first pick, Adopt uses all picks (composed via PIL on server). */}
              <div className="rounded-xl border border-white/10 bg-[#0F0F0F] p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/60">
                    Source render{pickedGenIds.length > 1 ? "s" : ""} · <span className="text-[#D4AF37]">{pickedGenIds.length} picked</span>
                  </div>
                  <div className="text-[10px] text-white/40">
                    {gallery.length ? `${gallery.length} in gallery` : (galleryLoaded ? "Gallery empty" : "Loading…")}
                  </div>
                </div>
                <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2 max-h-40 overflow-y-auto">
                  {item?.image_url && (
                    <SourceThumb
                      url={item.image_url}
                      genId={item.generation_id}
                      picked={pickedGenIds.includes(item.generation_id)}
                      pickIndex={pickedGenIds.indexOf(item.generation_id)}
                      onPick={() => togglePick(item.generation_id)}
                      pinned
                    />
                  )}
                  {gallery
                    .filter((g) => g.id !== item?.generation_id)
                    .slice(0, 32)
                    .map((g) => (
                      <SourceThumb
                        key={g.id}
                        url={g.image_url}
                        genId={g.id}
                        picked={pickedGenIds.includes(g.id)}
                        pickIndex={pickedGenIds.indexOf(g.id)}
                        onPick={() => togglePick(g.id)}
                      />
                    ))}
                </div>
                <div className="mt-2 text-[10px] text-white/45 leading-relaxed">
                  <span className="text-white/60">Tip:</span> pick multiple to <b className="text-white">adopt them as a composed multi-view</b> (free — up to 4 tiles, arranged in a labelled grid). <b className="text-white">Generate</b> always uses the first pick as the source.
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={runMultiview}
                    disabled={renderingMv || adopting || pickedGenIds.length === 0}
                    data-testid="pfs-mv-generate"
                    className="inline-flex items-center gap-1.5 text-xs px-4 py-2 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#F3CE5A] transition disabled:opacity-50"
                  >
                    {renderingMv
                      ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Rendering…</>
                      : <><Wand2 className="w-3.5 h-3.5" /> Generate multi-view · 1 credit</>}
                  </button>
                  <button
                    type="button"
                    onClick={adoptFromGallery}
                    disabled={adopting || renderingMv || pickedGenIds.length === 0}
                    data-testid="pfs-mv-adopt"
                    className="inline-flex items-center gap-1.5 text-xs px-4 py-2 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition disabled:opacity-40"
                  >
                    {adopting
                      ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Locking…</>
                      : <><Check className="w-3.5 h-3.5" /> {pickedGenIds.length > 1 ? `Adopt ${pickedGenIds.length} as multi-view · Free` : "Adopt selected as multi-view · Free"}</>}
                  </button>
                </div>
              </div>

              {/* Current multi-view preview */}
              {multiviewUrl ? (
                <div className="rounded-xl border border-white/10 bg-[#0F0F0F] overflow-hidden">
                  <img
                    src={`${BACKEND}${multiviewUrl}`}
                    alt="Multi-view"
                    className="w-full h-auto"
                    data-testid="pfs-mv-image"
                  />
                  <div className="p-3 border-t border-white/8 flex items-center justify-between">
                    <div className="text-[11px] text-emerald-300 flex items-center gap-1.5">
                      <Check className="w-3 h-3" /> Multi-view locked. Ready for technical drawing.
                    </div>
                    <button
                      type="button"
                      onClick={() => setTab("drawing")}
                      data-testid="pfs-mv-continue"
                      className="text-[11px] text-[#D4AF37] hover:text-[#F3CE5A] inline-flex items-center gap-1"
                    >
                      Continue to technical drawing →
                    </button>
                  </div>
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-white/15 bg-[#0F0F0F] p-10 text-center" data-testid="pfs-mv-empty">
                  <LayoutGrid className="w-8 h-8 text-[#D4AF37]/60 mx-auto mb-2" />
                  <p className="text-sm text-white/70 font-medium">No multi-view yet</p>
                  <p className="text-[12px] text-white/45 mt-1 max-w-md mx-auto leading-relaxed">
                    Pick a source render above and generate ({viewLabels.length}-tile grid + original) OR adopt an existing gallery render if it already shows the piece well.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


function TabBtn({ active, onClick, icon: Icon, label, testId, locked }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-lg transition ${
        active
          ? "bg-[#D4AF37]/15 text-[#D4AF37]"
          : locked
            ? "text-white/30 hover:text-white/45"
            : "text-white/60 hover:text-white"
      }`}
      title={locked ? "Generate a multi-view first" : undefined}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}{locked && !active ? " · locked" : ""}
    </button>
  );
}


function SourceThumb({ url, genId, picked, pickIndex, onPick, pinned }) {
  return (
    <button
      type="button"
      onClick={onPick}
      data-testid={`pfs-source-${genId}`}
      title={pinned ? "Item's default render" : "Gallery render"}
      className={`relative aspect-square rounded-lg overflow-hidden border transition ${
        picked ? "border-[#D4AF37] ring-1 ring-[#D4AF37]/50" : "border-white/10 hover:border-white/30"
      }`}
    >
      <img src={`${BACKEND}${url}`} alt="" className="w-full h-full object-cover" />
      {picked && (
        <span className="absolute top-1 right-1 min-w-4 h-4 px-1 rounded-full bg-[#D4AF37] text-black text-[9px] font-bold flex items-center justify-center">
          {typeof pickIndex === "number" && pickIndex >= 0 ? pickIndex + 1 : <Check className="w-2.5 h-2.5" strokeWidth={3} />}
        </span>
      )}
      {pinned && !picked && (
        <span className="absolute bottom-1 left-1 text-[8px] uppercase tracking-wider px-1 rounded bg-black/60 text-[#D4AF37]">
          Item
        </span>
      )}
    </button>
  );
}


function ArtifactPanel({
  testId, url, busy, busyLabel,
  emptyIcon: EmptyIcon, emptyTitle, emptyBody,
  buttonLabel, rerunLabel, onRun, savedProductType,
}) {
  return (
    <div className="space-y-4" data-testid={testId}>
      {url ? (
        <div className="rounded-xl border border-white/10 bg-[#0F0F0F] overflow-hidden">
          <img
            src={`${BACKEND}${url}`}
            alt="Generated sheet"
            className="w-full h-auto"
            data-testid={`${testId}-img`}
          />
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-white/15 bg-[#0F0F0F] p-10 text-center">
          <EmptyIcon className="w-8 h-8 text-[#D4AF37]/60 mx-auto mb-2" />
          <p className="text-sm text-white/70 font-medium">{emptyTitle}</p>
          <p className="text-[12px] text-white/45 mt-1 max-w-md mx-auto leading-relaxed">{emptyBody}</p>
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onRun}
          disabled={busy}
          data-testid={`${testId}-run`}
          className="inline-flex items-center gap-1.5 text-xs px-4 py-2 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#F3CE5A] transition disabled:opacity-50"
        >
          {busy ? (
            <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {busyLabel}</>
          ) : url ? (
            <><RefreshCw className="w-3.5 h-3.5" /> {rerunLabel}</>
          ) : (
            <><Wand2 className="w-3.5 h-3.5" /> {buttonLabel}</>
          )}
        </button>
        <span className="text-[10px] text-white/40 flex items-center gap-1">
          <ImageIcon className="w-3 h-3" /> Product type: {savedProductType}
        </span>
      </div>
    </div>
  );
}



// ---------------------------------------------------------------------------
// Interactive SVG Dimension Editor
// ---------------------------------------------------------------------------
// Users can drag out dimension arrows directly on the locked multi-view.
// The output is a list of `{from:{x,y}, to:{x,y}, label}` where the
// coords are normalised (0..1) to the underlying image. The backend
// deterministically burns these in with PIL BEFORE the Gemini call, so
// callouts land pixel-exact — no fresh AI hallucination.
//
// Interaction model:
//   * Click-drag on the image → new dimension callout. Both anchor points
//     sit ON the object; the dimension line auto-offsets outside the
//     product so nothing overlaps.
//   * Click a callout to select it — anchors + a MID handle appear.
//     Drag the mid handle perpendicular to push the dimension line
//     further out (positive or negative side).
//   * Endpoint circles retarget the anchors on the object.
function SvgDimensionEditor({ multiviewUrl, callouts, onChange }) {
  const wrapRef = useRef(null);
  const imgRef = useRef(null);
  const [imgSize, setImgSize] = useState({ w: 1, h: 1 });
  const [drawing, setDrawing] = useState(null);  // {x1,y1,x2,y2}
  const [selected, setSelected] = useState(-1);
  const [dragMode, setDragMode] = useState(null); // "from" | "to" | "offset" | null

  if (!multiviewUrl) return null;

  const toNorm = (evt) => {
    const rect = imgRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return {
      x: Math.max(0, Math.min(1, (evt.clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, (evt.clientY - rect.top) / rect.height)),
    };
  };

  const onImgLoad = () => {
    const el = imgRef.current;
    if (!el) return;
    setImgSize({ w: el.clientWidth, h: el.clientHeight });
  };

  const onCanvasDown = (e) => {
    if (e.button !== 0) return;
    // Only start drawing if the click hit the underlying image (not a handle).
    if (e.target.dataset?.handle) return;
    const p = toNorm(e);
    setDrawing({ x1: p.x, y1: p.y, x2: p.x, y2: p.y });
    setSelected(-1);
  };

  const onCanvasMove = (e) => {
    if (drawing) {
      const p = toNorm(e);
      setDrawing((d) => ({ ...d, x2: p.x, y2: p.y }));
    } else if (dragMode && selected >= 0) {
      const p = toNorm(e);
      const next = callouts.slice();
      const c = { ...next[selected] };
      if (dragMode === "from") c.from = p;
      else if (dragMode === "to") c.to = p;
      else if (dragMode === "offset") {
        // Compute the signed perpendicular distance from the mouse to
        // the anchor line, normalised by the image diagonal. Right-hand
        // side of from→to is positive.
        const W = imgSize.w || 1, H = imgSize.h || 1;
        const diag = Math.hypot(W, H);
        const x1 = c.from.x * W, y1 = c.from.y * H;
        const x2 = c.to.x * W, y2 = c.to.y * H;
        const dx = x2 - x1, dy = y2 - y1;
        const L = Math.hypot(dx, dy) || 1;
        const nx = -dy / L, ny = dx / L;
        const px = p.x * W, py = p.y * H;
        c.offset = ((px - (x1 + x2) / 2) * nx + (py - (y1 + y2) / 2) * ny) / diag;
      }
      next[selected] = c;
      onChange(next);
    }
  };

  const onCanvasUp = () => {
    if (drawing) {
      const dx = drawing.x2 - drawing.x1;
      const dy = drawing.y2 - drawing.y1;
      if (Math.hypot(dx, dy) > 0.02) {
        // Default offset ~8% of the diagonal so the dimension line
        // lands OUTSIDE the object on first drop. User can drag it in
        // or flip sides with the mid handle.
        onChange([
          ...callouts,
          {
            from: { x: drawing.x1, y: drawing.y1 },
            to: { x: drawing.x2, y: drawing.y2 },
            offset: 0.08,
            label: "10 mm",
          },
        ]);
        setSelected(callouts.length);
      }
      setDrawing(null);
    }
    setDragMode(null);
  };

  const updateLabel = (idx, label) => {
    const next = callouts.slice();
    next[idx] = { ...next[idx], label };
    onChange(next);
  };

  const nudgeOffset = (idx, delta) => {
    const next = callouts.slice();
    const cur = Number(next[idx]?.offset ?? 0);
    const bounded = Math.max(-0.35, Math.min(0.35, cur + delta));
    next[idx] = { ...next[idx], offset: bounded };
    onChange(next);
  };

  const flipOffset = (idx) => {
    const next = callouts.slice();
    const cur = Number(next[idx]?.offset ?? 0);
    const flipped = cur === 0 ? 0.08 : -cur;
    next[idx] = { ...next[idx], offset: flipped };
    onChange(next);
  };

  const removeCallout = (idx) => {
    onChange(callouts.filter((_, i) => i !== idx));
    setSelected(-1);
  };

  const clearAll = () => {
    onChange([]);
    setSelected(-1);
  };

  const W = imgSize.w || 1;
  const H = imgSize.h || 1;
  const DIAG = Math.hypot(W, H);

  return (
    <div className="rounded-xl border border-white/10 bg-[#0F0F0F] overflow-hidden" data-testid="pfs-svg-editor">
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/8">
        <div className="flex items-center gap-2">
          <MousePointer2 className="w-3.5 h-3.5 text-[#D4AF37]" />
          <span className="text-[11px] uppercase tracking-[0.22em] text-white/70">Manual Callouts · Drag on image</span>
          <span className="text-[10px] text-white/40">{callouts.length} placed</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={clearAll}
            disabled={callouts.length === 0}
            data-testid="pfs-svg-clear"
            className="text-[10px] px-2 py-1 rounded-full border border-white/15 text-white/60 hover:text-white hover:border-white/30 transition disabled:opacity-30"
          >
            Clear all
          </button>
        </div>
      </div>
      <div ref={wrapRef} className="relative select-none" style={{ userSelect: "none" }}>
        <img
          ref={imgRef}
          src={`${BACKEND}${multiviewUrl}`}
          alt="Multi-view"
          className="w-full h-auto block"
          onLoad={onImgLoad}
          draggable={false}
        />
        <svg
          className="absolute inset-0 w-full h-full cursor-crosshair"
          onMouseDown={onCanvasDown}
          onMouseMove={onCanvasMove}
          onMouseUp={onCanvasUp}
          onMouseLeave={onCanvasUp}
        >
          <defs>
            <marker id="arrow-end" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#D4AF37" />
            </marker>
          </defs>

          {callouts.map((c, i) => {
            const x1 = c.from.x * W, y1 = c.from.y * H;
            const x2 = c.to.x * W, y2 = c.to.y * H;
            const dx = x2 - x1, dy = y2 - y1;
            const L = Math.hypot(dx, dy) || 1;
            const nx = -dy / L, ny = dx / L;
            const offset = Number(c.offset ?? 0);
            const push = offset * DIAG;
            const dx1 = x1 + nx * push, dy1 = y1 + ny * push;
            const dx2 = x2 + nx * push, dy2 = y2 + ny * push;
            const mx = (dx1 + dx2) / 2, my = (dy1 + dy2) / 2;
            const active = selected === i;
            const goldStroke = active ? "#F3CE5A" : "#D4AF37";
            return (
              <g key={i}>
                {/* Extension lines from each anchor to the dimension line */}
                {Math.abs(push) > 2 && (
                  <>
                    <line
                      x1={x1} y1={y1} x2={dx1 + nx * (push >= 0 ? 6 : -6)} y2={dy1 + ny * (push >= 0 ? 6 : -6)}
                      stroke={goldStroke} strokeWidth="1" strokeDasharray="3 3" opacity="0.75"
                      style={{ pointerEvents: "none" }}
                    />
                    <line
                      x1={x2} y1={y2} x2={dx2 + nx * (push >= 0 ? 6 : -6)} y2={dy2 + ny * (push >= 0 ? 6 : -6)}
                      stroke={goldStroke} strokeWidth="1" strokeDasharray="3 3" opacity="0.75"
                      style={{ pointerEvents: "none" }}
                    />
                  </>
                )}
                {/* Dimension line — this is the one that never overlaps the product */}
                <line
                  x1={dx1} y1={dy1} x2={dx2} y2={dy2}
                  stroke={goldStroke}
                  strokeWidth={active ? 2.5 : 2}
                  markerStart="url(#arrow-end)"
                  markerEnd="url(#arrow-end)"
                  style={{ cursor: "pointer" }}
                  onMouseDown={(e) => { e.stopPropagation(); setSelected(i); }}
                />
                <rect
                  x={mx - 34} y={my - 12} width={68} height={20} rx={4}
                  fill="rgba(20,20,20,0.9)"
                  stroke={goldStroke}
                  onMouseDown={(e) => { e.stopPropagation(); setSelected(i); }}
                  style={{ cursor: "pointer" }}
                />
                <text
                  x={mx} y={my + 3} textAnchor="middle" fontSize="11" fill="#F3CE5A" fontWeight="600"
                  style={{ pointerEvents: "none", userSelect: "none" }}
                >
                  {c.label || "—"}
                </text>
                {active && (
                  <>
                    {/* Anchor endpoints — grab to retarget on the product */}
                    <circle
                      cx={x1} cy={y1} r={6} fill="#F3CE5A" stroke="#0B0B0B" strokeWidth="1.5"
                      data-handle="from"
                      onMouseDown={(e) => { e.stopPropagation(); setDragMode("from"); setSelected(i); }}
                      style={{ cursor: "grab" }}
                    />
                    <circle
                      cx={x2} cy={y2} r={6} fill="#F3CE5A" stroke="#0B0B0B" strokeWidth="1.5"
                      data-handle="to"
                      onMouseDown={(e) => { e.stopPropagation(); setDragMode("to"); setSelected(i); }}
                      style={{ cursor: "grab" }}
                    />
                    {/* Offset handle — the money handle. Drag perpendicular to push
                        the dimension line off the product. */}
                    <circle
                      cx={mx + nx * 14} cy={my + ny * 14} r={7}
                      fill="#0B0B0B" stroke="#F3CE5A" strokeWidth="2"
                      data-handle="offset"
                      onMouseDown={(e) => { e.stopPropagation(); setDragMode("offset"); setSelected(i); }}
                      style={{ cursor: "grab" }}
                    />
                    <line
                      x1={mx} y1={my} x2={mx + nx * 14} y2={my + ny * 14}
                      stroke="#F3CE5A" strokeWidth="1.2" opacity="0.7"
                      style={{ pointerEvents: "none" }}
                    />
                  </>
                )}
              </g>
            );
          })}

          {drawing && (
            <line
              x1={drawing.x1 * W} y1={drawing.y1 * H}
              x2={drawing.x2 * W} y2={drawing.y2 * H}
              stroke="#F3CE5A" strokeWidth="2" strokeDasharray="4 3"
              markerStart="url(#arrow-end)" markerEnd="url(#arrow-end)"
            />
          )}
        </svg>
      </div>
      <div className="px-3 py-2 border-t border-white/8 flex flex-wrap items-center gap-2 min-h-[42px]">
        {selected >= 0 && callouts[selected] ? (
          <>
            <span className="text-[10px] uppercase tracking-[0.22em] text-white/50">Callout {selected + 1}</span>
            <input
              type="text"
              value={callouts[selected].label || ""}
              onChange={(e) => updateLabel(selected, e.target.value)}
              placeholder="e.g. 9 mm"
              data-testid="pfs-svg-label"
              className="bg-[#141414] border border-white/10 rounded-md px-2 py-1 text-xs text-white outline-none focus:border-[#D4AF37]/50 w-32"
            />
            <div className="inline-flex items-center rounded-md border border-white/12 bg-[#141414] overflow-hidden">
              <span className="px-2 text-[9px] uppercase tracking-[0.18em] text-white/45">Offset</span>
              <button
                type="button"
                onClick={() => nudgeOffset(selected, -0.02)}
                data-testid="pfs-svg-offset-in"
                className="px-2 py-1 text-[11px] text-white/70 hover:bg-white/5 transition"
                title="Pull line closer to the object"
              >−</button>
              <span className="px-2 text-[10px] text-[#D4AF37] tabular-nums w-10 text-center">
                {(Number(callouts[selected].offset ?? 0) * 100).toFixed(0)}%
              </span>
              <button
                type="button"
                onClick={() => nudgeOffset(selected, 0.02)}
                data-testid="pfs-svg-offset-out"
                className="px-2 py-1 text-[11px] text-white/70 hover:bg-white/5 transition"
                title="Push line further from the object"
              >+</button>
              <button
                type="button"
                onClick={() => flipOffset(selected)}
                data-testid="pfs-svg-offset-flip"
                className="px-2 py-1 text-[10px] border-l border-white/12 text-[#D4AF37] hover:bg-white/5 transition"
                title="Flip the dimension line to the other side"
              >Flip</button>
            </div>
            <button
              type="button"
              onClick={() => removeCallout(selected)}
              data-testid="pfs-svg-delete"
              className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md text-red-300 hover:bg-red-500/10 transition"
            >
              <Trash2 className="w-3 h-3" /> Delete
            </button>
            <span className="text-[10px] text-white/35 ml-auto">Drag the ring handle to move the dimension line off the product.</span>
          </>
        ) : (
          <span className="text-[10px] text-white/40 inline-flex items-center gap-1">
            <Plus className="w-3 h-3" /> Click-drag on the image to place a dimension. Selection reveals label, ± offset, and endpoint handles.
          </span>
        )}
      </div>
    </div>
  );
}
